// Where to send the user after login when an auth-gated page bounced them.
//
// localStorage, NOT sessionStorage: iOS Safari can jettison a backgrounded tab
// and restore it with empty sessionStorage (seen in prod 2026-07-24 - a user
// bounced off /season-long/mock-draft lost the saved path while switching to
// Messages for the OTP and landed on /contests). localStorage survives tab
// restores. The timestamp expiry keeps a stale path from hijacking an
// unrelated login days later.

const KEY = 'intendedRedirectPath';
const MAX_AGE_MS = 30 * 60 * 1000;
const isSafeInternalPath = (path: unknown): path is string => (
  typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')
);

export function saveIntendedPath(path: string): boolean {
  if (!isSafeInternalPath(path)) return false;
  try {
    localStorage.setItem(KEY, JSON.stringify({ path, savedAt: Date.now() }));
    return true;
  } catch {
    // Storage unavailable (private mode) - ordinary login falls back to Home.
    return false;
  }
}

export function consumeIntendedPath(): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    localStorage.removeItem(KEY);
    const { path, savedAt } = JSON.parse(raw);
    // Same-origin relative paths only, and only recent ones.
    if (!isSafeInternalPath(path)) return null;
    if (typeof savedAt !== 'number' || Date.now() - savedAt > MAX_AGE_MS) return null;
    return path;
  } catch {
    return null;
  }
}
